import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuote } from '../contexts/QuoteContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Search, Package, Plus, Minus, X, Loader2, ShoppingCart, ChevronDown, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StepIndicator = ({ currentStep }) => (
    <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((step) => (
            <div
                key={step}
                className={`h-2 rounded-full transition-all duration-300 ${
                    step === currentStep ? 'w-8 bg-brand-lime' : step < currentStep ? 'w-2 bg-brand-lime' : 'w-2 bg-brand-gray-200'
                }`}
            />
        ))}
    </div>
);

const fmt = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

const SelectPack = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { customerDetails, selectedItems, addItem, removeItem, updateQuantity } = useQuote();
    const [packs, setPacks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [expandedPack, setExpandedPack] = useState(null);

    useEffect(() => {
        if (!customerDetails.company_name || !customerDetails.email) {
            navigate('/');
            return;
        }
        fetchData();
    }, [customerDetails, navigate]);

    const fetchData = async () => {
        try {
            const [packsRes, catsRes] = await Promise.all([
                axios.get(`${API}/spares`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/spares-categories`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPacks(packsRes.data);
            setCategories(catsRes.data);
        } catch {
            toast.error('Failed to load spares packs');
        } finally {
            setLoading(false);
        }
    };

    const filteredPacks = packs.filter(pack => {
        const matchesSearch = pack.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            pack.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' ||
            (selectedCategory === 'uncategorised' ? !pack.category_id : pack.category_id === selectedCategory);
        return matchesSearch && matchesCategory;
    });

    const isSelected = (packId) => selectedItems.some(i => i.pack.id === packId);

    const handleTogglePack = (pack) => {
        if (isSelected(pack.id)) {
            removeItem(pack.id);
        } else {
            if (selectedItems.length >= 10) {
                toast.error('Maximum 10 packs per quote');
                return;
            }
            addItem(pack);
        }
    };

    const handleContinue = () => {
        if (selectedItems.length === 0) {
            toast.error('Please select at least one spares pack');
            return;
        }
        navigate('/review');
    };

    const runningTotal = selectedItems.reduce((sum, i) => sum + (i.pack.price * i.quantity), 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto animate-fade-in">
            <StepIndicator currentStep={2} />

            <Card className="shadow-lg border-0">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold font-heading">
                        Select Spares Packs
                    </CardTitle>
                    <CardDescription>
                        Choose packs for {customerDetails.company_name} (max 10)
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                        <Input
                            type="text"
                            placeholder="Search packs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-12 pl-10"
                            data-testid="pack-search-input"
                        />
                    </div>

                    {/* Category Filter Tabs */}
                    {categories.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="engineer-category-filter">
                            <button
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                    selectedCategory === 'all'
                                        ? 'bg-brand-lime text-black'
                                        : 'bg-brand-gray-100 text-brand-gray-600 hover:bg-brand-gray-200'
                                }`}
                                onClick={() => setSelectedCategory('all')}
                                data-testid="engineer-category-all"
                            >
                                All
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                        selectedCategory === cat.id
                                            ? 'bg-brand-lime text-black'
                                            : 'bg-brand-gray-100 text-brand-gray-600 hover:bg-brand-gray-200'
                                    }`}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    data-testid={`engineer-category-${cat.id}`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Packs List */}
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                        {filteredPacks.length === 0 ? (
                            <div className="text-center py-12">
                                <Package className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                                <p className="text-brand-gray-500">No packs found</p>
                            </div>
                        ) : (
                            filteredPacks.map(pack => {
                                const selected = isSelected(pack.id);
                                const expanded = expandedPack === pack.id;
                                return (
                                    <div
                                        key={pack.id}
                                        className={`rounded-lg border-2 transition-all ${
                                            selected
                                                ? 'border-brand-lime bg-brand-lime/5'
                                                : 'border-brand-gray-200 hover:border-brand-lime/40'
                                        }`}
                                        data-testid={`spare-pack-${pack.id}`}
                                    >
                                        <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpandedPack(expanded ? null : pack.id)}>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-brand-charcoal text-sm line-clamp-1">{pack.name}</p>
                                                <p className={`text-xs text-brand-gray-500 ${expanded ? '' : 'line-clamp-1'}`}>{pack.description}</p>
                                            </div>
                                            <span className="text-sm font-bold text-brand-charcoal whitespace-nowrap">{fmt(pack.price)}</span>
                                            <ChevronDown className={`h-4 w-4 flex-shrink-0 text-brand-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                                            {selected ? (
                                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 flex-shrink-0" onClick={(e) => { e.stopPropagation(); removeItem(pack.id); }} data-testid={`remove-pack-${pack.id}`}>
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="outline" className="h-8 w-8 p-0 flex-shrink-0" onClick={(e) => { e.stopPropagation(); handleTogglePack(pack); }} data-testid={`add-pack-${pack.id}`}>
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                        {expanded && (
                                            <div className="px-3 pb-3 pt-0">
                                                <p className="text-xs text-brand-gray-600 whitespace-pre-line bg-brand-gray-50 rounded p-2" data-testid={`pack-description-${pack.id}`}>{pack.description}</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Selected Items Cart */}
                    {selectedItems.length > 0 && (
                        <div className="p-4 bg-brand-lime/10 rounded-lg border border-brand-lime/30 space-y-3" data-testid="selected-items-cart">
                            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brand-gray-500">
                                <ShoppingCart className="h-4 w-4" />
                                {selectedItems.length} pack{selectedItems.length > 1 ? 's' : ''} selected
                            </div>
                            <div className="space-y-2">
                                {selectedItems.map(({ pack, quantity }) => (
                                    <div key={pack.id} className="flex items-center gap-2 bg-white/60 rounded-md p-2">
                                        <span className="flex-1 text-sm font-medium text-brand-charcoal line-clamp-1">{pack.name}</span>
                                        <div className="flex items-center gap-1" data-testid={`qty-control-${pack.id}`}>
                                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQuantity(pack.id, quantity - 1)} disabled={quantity <= 1}>
                                                <Minus className="h-3 w-3" />
                                            </Button>
                                            <Input
                                                type="number"
                                                value={quantity}
                                                onChange={(e) => updateQuantity(pack.id, parseInt(e.target.value) || 1)}
                                                className="h-7 w-12 text-center text-sm p-0"
                                                min={1}
                                                max={99}
                                                data-testid={`qty-input-${pack.id}`}
                                            />
                                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQuantity(pack.id, quantity + 1)} disabled={quantity >= 99}>
                                                <Plus className="h-3 w-3" />
                                            </Button>
                                        </div>
                                        <span className="text-sm font-bold whitespace-nowrap w-20 text-right">{fmt(pack.price * quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-brand-lime/30">
                                <span className="text-sm font-bold text-brand-charcoal">Running Total (Ex VAT)</span>
                                <span className="text-lg font-bold font-heading text-brand-charcoal">{fmt(runningTotal)}</span>
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="flex gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => navigate('/')} className="flex-1 h-12" data-testid="back-to-customer-btn">
                            <ArrowLeft className="h-5 w-5 mr-2" /> Back
                        </Button>
                        <Button
                            type="button"
                            onClick={handleContinue}
                            disabled={selectedItems.length === 0}
                            className="flex-1 h-12 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide"
                            data-testid="continue-to-review-btn"
                        >
                            Review Quote
                            <ArrowRight className="h-5 w-5 ml-2" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SelectPack;
